
import Vue from 'vue';
import Component from 'vue-class-component';
import { getModule } from 'vuex-module-decorators';
import GlobalStore from '@/store/modules/global';
import { Auth } from '@aws-amplify/auth';
import { Inject, Watch } from 'vue-property-decorator';
import { Container } from 'inversify';
import SERVICE_IDENTIFIER from '@/flightpath-components/services/identifiers';
import { Convert, FlatUser, Group, SingleUser, SingleUserWithGroup, User, UsersWithGroup } from '@/flightpath-components/models/CognitoUser';
import { AxiosResponse } from 'axios';
import { FlightPathServiceInterface } from '@/flightpath-components/services/FlightPathServiceInterface';
// Define the component in class-style
/* eslint-disable @typescript-eslint/no-explicit-any */
@Component({ name: 'Users' })
export default class Users extends Vue {
  public globalStore = getModule(GlobalStore, this.$store);
  signedIn = false;
  dialog = false;
  dialogDelete = false;
  dialogResend = false;
  errorState = false;
  errorMessage = '';
  requestInProgress = false;
  totalUsers = 0;
  loading = true;
  datapointsPerPage = 10;
  datapointsCurrentPage = 1;
  pageTokens = new Map<number, string>();
  groupSelection = [Group.Managers, Group.Operators, Group.Dealers];
  private options: any = {
    itemsPerPage: this.datapointsPerPage,
    page: this.datapointsCurrentPage
  };

  @Watch('options.page')
  onPageChange(value: number): void {
    this.getDataFromApi(value);
  }

  @Watch('options.itemsPerPage')
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPerPageChange(value: number, oldValue: number): void {
    this.pageTokens.clear();
    if (this.options.page == 1) this.getDataFromApi(1);
    this.options.page = 1;
  }

  @Watch('dialog')
  onDialogChange(val: any) {
    return val || this.close();
  }
  @Watch('dialogDelete')
  onDialogDeleteChange(val: any) {
    return val || this.close();
  }
  @Watch('dialogResend')
  onDialogResendChange(val: any) {
    return val || this.close();
  }
  headers = [
    {
      text: this.$t('views.users.headers.email'),
      value: 'Email',
      align: 'left'
    },
    { text: this.$t('views.users.headers.status'), value: 'UserStatus', align: 'left' },
    { text: this.$t('views.users.headers.enabled'), value: 'Enabled', align: 'left' },
    { text: this.$t('views.users.headers.group'), value: 'DisplayGroups', align: 'left' },
    { text: this.$t('views.users.headers.actions'), value: 'actions', sortable: false }
  ];

  windowSize = {
    x: 0,
    y: 0
  };

  editedIndex = -1;
  editedItem: FlatUser = {
    Email: '',
    UserStatus: '',
    Enabled: true,
    UserCreateDate: new Date(),
    UserLastModifiedDate: new Date(),
    Username: '',
    EmailVerified: true,
    DisplayGroups: '',
    Groups: [],
    SelectedGroup: Group.Planners
  };
  defaultItem: FlatUser = {
    Email: '',
    UserStatus: '',
    Enabled: true,
    UserCreateDate: new Date(),
    UserLastModifiedDate: new Date(),
    Username: '',
    EmailVerified: true,
    DisplayGroups: '',
    Groups: [],
    SelectedGroup: Group.Planners
  };

  users: Array<FlatUser> = [];

  getDataFromApi(page: number) {
    this.loading = true;
    this.getUsers(page).then((data) => {
      this.users = data;
      this.loading = false;
    });
  }

  async getUsers(page: number): Promise<FlatUser[]> {
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    let token = '';
    if (this.pageTokens.has(page)) token = this.pageTokens.get(page)?.toString() ?? '';
    const response = await this.flightPathService?.getAllUsers(this.options.itemsPerPage > 0 ? this.options.itemsPerPage : 50, token, '');
    const users = response?.data;
    return this.convertCognitoUserToFlatUser(JSON.stringify(users), page);
  }

  initialize() {
    this.getDataFromApi(1);
  }

  async axiosRequestHandler(callbackFn: () => Promise<AxiosResponse> | undefined): Promise<AxiosResponse> {
    let response: AxiosResponse | undefined;
    try {
      this.requestInProgress = true;
      response = await callbackFn();
    } catch (error) {
      console.error(error.response);
      response = error.response;

      this.errorState = true;
      this.errorMessage = error.response.data;
    } finally {
      this.requestInProgress = false;
    }
    return response || ({ status: 500, data: 'Unknown error' } as AxiosResponse);
  }

  convertCognitoUserToFlatUser(cognitoData: string, page: number): Array<FlatUser> {
    const userRequest = Convert.toUserRequest(cognitoData);

    if (userRequest.PaginationToken == null) {
      this.totalUsers = this.options.itemsPerPage * (page - 1) + userRequest.UsersWithGroups.length;
    } else {
      this.totalUsers = this.options.itemsPerPage * (page + 1);
    }
    if (!this.pageTokens.has(page + 1)) this.pageTokens.set(page + 1, userRequest.PaginationToken);
    const flatUsers: Array<FlatUser> = [];
    console.log(userRequest);
    userRequest.UsersWithGroups.forEach((user) => {
      if (user != null) {
        const flatUser: FlatUser = this.generateFlatUser(user);
        flatUsers.push(flatUser);
      }
    });

    return flatUsers;
  }

  private generateFlatUser(user: UsersWithGroup): FlatUser {
    const displayGroups: string[] = [];
    user.Groups?.forEach((x) => {
      // don't need to show flightpath admin
      if (x != Group.FlightPathAdmin) {
        if (x === Group.AccountOwner) {
          displayGroups.push('Account Owner');
        } else {
          const translationKey = 'user_group.' + x;
          displayGroups.push(this.$te(translationKey) ? this.$t(translationKey).toString() : x);
        }
      }
    });
    return {
      Username: user.User?.Username ?? '',
      Enabled: user.User?.Enabled ?? false,
      UserCreateDate: user.User?.UserCreateDate ?? new Date(),
      UserLastModifiedDate: user.User?.UserLastModifiedDate ?? new Date(),
      UserStatus: user.User?.UserStatus.Value ?? '',
      Email: user.User != null ? this.userAttribute(user.User, 'email') : '',
      EmailVerified: user.User != null ? (this.userAttribute(user.User, 'email_verified') == 'true' ? true : false) : false,
      DisplayGroups: displayGroups.join(', ') ?? '',
      Groups: user.Groups as [Group],
      SelectedGroup:
        user.Groups?.filter((value) => {
          return value != Group.Admins && value != Group.Users;
        }).toString() ?? ''
    };
  }

  private generateFlatSingleUser(user: SingleUserWithGroup): FlatUser {
    const displayGroups: string[] = [];
    user.Groups?.forEach((x) => {
      // don't need to show flightpath admin
      if (x != Group.FlightPathAdmin) {
        if (x === Group.AccountOwner) {
          displayGroups.push('Account Owner');
        } else {
          const translationKey = 'user_group.' + x;
          displayGroups.push(this.$te(translationKey) ? this.$t(translationKey).toString() : x);
        }
      }
    });
    return {
      Username: user.User?.Username ?? '',
      Enabled: user.User?.Enabled ?? false,
      UserCreateDate: user.User?.UserCreateDate ?? new Date(),
      UserLastModifiedDate: user.User?.UserLastModifiedDate ?? new Date(),
      UserStatus: user.User?.UserStatus.Value ?? '',
      Email: user.User != null ? this.userLocalAttribute(user.User, 'email') : '',
      EmailVerified: user.User != null ? (this.userLocalAttribute(user.User, 'email_verified') == 'true' ? true : false) : false,
      DisplayGroups: displayGroups.join(', ') ?? '',
      Groups: user.Groups as [Group],
      SelectedGroup:
        user.Groups?.filter((value) => {
          return value != Group.Admins && value != Group.Users;
        }).toString() ?? ''
    };
  }

  userAttribute(user: User, attribute: string): string {
    let returnValue = '';
    user.Attributes?.forEach((value) => {
      if (value.Name === attribute) {
        returnValue = value.Value;
      }
    });
    return returnValue;
  }

  userLocalAttribute(user: SingleUser, attribute: string): string {
    let returnValue = '';
    user.UserAttributes?.forEach((value) => {
      if (value.Name === attribute) {
        returnValue = value.Value;
      }
    });
    return returnValue;
  }
  editItem(item: FlatUser) {
    this.editedIndex = this.users.indexOf(item);
    this.editedItem = Object.assign({}, item);
    this.dialog = true;
  }

  deleteItem(item: FlatUser) {
    this.editedIndex = this.users.indexOf(item);
    this.editedItem = Object.assign({}, item);
    this.dialogDelete = true;
  }

  resendItem(item: FlatUser) {
    this.editedIndex = this.users.indexOf(item);
    this.editedItem = Object.assign({}, item);
    this.dialogResend = true;
  }

  async deleteItemConfirm() {
    await this.axiosRequestHandler(() => this.flightPathService?.deleteUser(this.editedItem.Email));
    if (!this.errorState) {
      this.users.splice(this.editedIndex, 1);
      this.closeDelete();
    }
  }

  async ResendItemConfirm() {
    await this.axiosRequestHandler(() => this.flightPathService?.inviteUser(this.editedItem.Email));
    if (!this.errorState) {
      this.users.splice(this.editedIndex, 1, this.editedItem);
      this.closeResend();
    }
  }

  close() {
    this.dialog = false;
    this.$nextTick(() => {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    });
  }

  closeDelete() {
    this.dialogDelete = false;
    this.$nextTick(() => {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    });
  }
  closeResend() {
    this.dialogResend = false;
    this.$nextTick(() => {
      this.editedItem = Object.assign({}, this.defaultItem);
      this.editedIndex = -1;
    });
  }
  async save() {
    let issues = false;
    if (this.editedIndex === -1) {
      issues = await this.AddNew();
    } else {
      const originalItem = this.users[this.editedIndex];

      issues = await this.updateUser(issues);

      if (originalItem.Enabled != this.editedItem.Enabled) {
        if (this.editedItem.Enabled) {
          issues = await this.enableUser(issues);
        } else {
          issues = await this.disableUser(issues);
        }
      }
    }
    if (!issues) this.close();
  }

  // @Inject()
  private flightPathService: FlightPathServiceInterface | undefined = undefined;
  // IoC container provided from App.ts is injected here
  @Inject('container')
  private container!: Container;

  private async enableUser(issues: boolean) {
    const enableResponse = await this.axiosRequestHandler(() => this.flightPathService?.enableUser(this.editedItem.Email));
    if (enableResponse?.status != 200) {
      issues = true;
    }
    return issues;
  }

  private async disableUser(issues: boolean) {
    const disableResponse = await this.axiosRequestHandler(() => this.flightPathService?.disableUser(this.editedItem.Email));
    if (disableResponse?.status != 200) {
      issues = true;
    }
    return issues;
  }

  private async updateUser(issues: boolean) {
    const groups = this.editedItem.Groups.filter((value) => {
      return value == Group.Admins || value == Group.Users;
    });
    const selectedGroup = this.editedItem.SelectedGroup as Group;
    groups.push(selectedGroup);

    const updateUserResponse = await this.axiosRequestHandler(() => this.flightPathService?.updateUser(this.editedItem.Email, groups));
    if (updateUserResponse?.status != 200) {
      issues = true;
    } else {
      try {
        const getUserResponse = await this.axiosRequestHandler(() => this.flightPathService?.getUser(this.editedItem.Email));
        if (getUserResponse?.status == 200) {
          const newUser = Convert.toSingleUsersWithGroup(JSON.stringify(getUserResponse.data));
          if (newUser != null) {
            Object.assign(this.users[this.editedIndex], this.generateFlatSingleUser(newUser));
          }
        }
      } catch (ex) {
        console.error(ex);
      }
    }
    return issues;
  }

  private async AddNew(): Promise<boolean> {
    let issues = false;
    const addUserResponse = await this.axiosRequestHandler(() => this.flightPathService?.addUser(this.editedItem.Email, this.editedItem.SelectedGroup));
    if (addUserResponse?.status != 200) {
      issues = true;
    } else {
      try {
        const getUserResponse = await this.axiosRequestHandler(() => this.flightPathService?.getUser(this.editedItem.Email));
        if (getUserResponse?.status == 200) {
          const newUser = Convert.toSingleUsersWithGroup(JSON.stringify(getUserResponse.data));
          if (newUser != null) {
            this.users.push(this.generateFlatSingleUser(newUser));
          }
        }
      } catch (ex) {
        console.error(ex);
      }
    }
    if (this.editedItem.Enabled == false) {
      const disableResponse = await this.axiosRequestHandler(() => this.flightPathService?.disableUser(this.editedItem.Email));
      if (disableResponse?.status != 200) {
        issues = true;
        console.error(disableResponse);
      }
    }
    return issues;
  }

  get formTitle() {
    return this.editedIndex === -1 ? this.$t('views.users.invite_new_user') : this.$t('views.users.edit_user');
  }

  created(): void {
    this.flightPathService = this.container.get<FlightPathServiceInterface>(SERVICE_IDENTIFIER.FLIGHT_PATH);
  }

  // Class properties will be component data
  onResize() {
    this.windowSize = { x: window.innerWidth, y: window.innerHeight };
  }
  async mounted() {
    this.onResize();
    if (this.flightPathService != undefined) await this.flightPathService.updateToken();
    this.initialize();
  }
  beforeCreate() {
    Auth.currentAuthenticatedUser()
      .then((user) => {
        if (user != undefined) {
          this.signedIn = true;
        }
      })
      .catch(() => console.log('not signed in...'));

    Auth.currentSession()
      .then((session) => {
        if (session != undefined) {
          console.log(session);
        }
      })
      .catch(() => console.log('not signed in...'));
  }
}
